import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Paper,
  TextInput,
  Textarea,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useBillingScenariosAdmin,
  useCreateBillingScenario,
  useUpdateBillingScenario,
  useDeleteBillingScenario,
  useToggleBillingScenarioActive,
} from "../../../hooks/admin/useBillingScenarios";
import { BillingScenario } from "../../../services/admin/billingscenarios.service";

interface FormValues {
  name: string;
  description: string;
}

interface BillingScenarioFilters {
  status?: "active" | "inactive" | "all";
}

const ITEMS_PER_PAGE = 20;

export default function BillingScenariosAdminPage() {
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [
    toggleModalOpened,
    { open: openToggleModal, close: closeToggleModal },
  ] = useDisclosure(false);

  const [editing, setEditing] = useState<BillingScenario | null>(null);
  const [itemToDelete, setItemToDelete] = useState<BillingScenario | null>(
    null
  );
  const [itemToToggle, setItemToToggle] = useState<BillingScenario | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<BillingScenarioFilters>({});
  const [pendingFilters, setPendingFilters] = useState<BillingScenarioFilters>(
    {}
  );
  const [page, setPage] = useState(1);

  const {
    data: billingScenarios = [],
    isLoading,
    refetch,
  } = useBillingScenariosAdmin();
  const createScenario = useCreateBillingScenario();
  const updateScenario = useUpdateBillingScenario();
  const deleteScenario = useDeleteBillingScenario();
  const toggleActive = useToggleBillingScenarioActive();

  const form = useForm<FormValues>({
    initialValues: {
      name: "",
      description: "",
    },
    validate: {
      name: (value) => (!value?.trim() ? "Name is required" : null),
    },
  });

  const filteredData = useMemo(() => {
    let data = billingScenarios;

    if (debouncedSearch) {
      const lower = debouncedSearch.toLowerCase();
      data = data.filter(
        (item) =>
          item.name.toLowerCase().includes(lower) ||
          (item.description || "").toLowerCase().includes(lower)
      );
    }

    if (filters.status === "active") {
      data = data.filter((item) => item.isActive);
    } else if (filters.status === "inactive") {
      data = data.filter((item) => !item.isActive);
    }

    return data;
  }, [billingScenarios, debouncedSearch, filters]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  const hasActiveFilters = !!(filters.status && filters.status !== "all");

  const handleAdd = () => {
    setEditing(null);
    form.reset();
    openFormModal();
  };

  const handleEdit = (item: BillingScenario) => {
    setEditing(item);
    form.setValues({
      name: item.name,
      description: item.description || "",
    });
    openFormModal();
  };

  const handleSubmit = async (values: FormValues) => {
    if (editing) {
      await updateScenario.mutateAsync({
        id: editing.id,
        name: values.name,
        description: values.description || undefined,
      });
    } else {
      await createScenario.mutateAsync({
        name: values.name,
        description: values.description || undefined,
      });
    }
    closeFormModal();
    form.reset();
  };

  const handleDelete = (item: BillingScenario) => {
    setItemToDelete(item);
    openDeleteModal();
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      await deleteScenario.mutateAsync(itemToDelete.id);
      closeDeleteModal();
      setItemToDelete(null);
    }
  };

  const handleToggleActive = (item: BillingScenario) => {
    setItemToToggle(item);
    openToggleModal();
  };

  const confirmToggle = async () => {
    if (itemToToggle) {
      await toggleActive.mutateAsync({
        id: itemToToggle.id,
        isActive: !itemToToggle.isActive,
      });
      closeToggleModal();
      setItemToToggle(null);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Billing Scenarios</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage billing scenarios used throughout the system
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name or description..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status && filters.status !== "all" && (
                      <Badge
                        variant="light"
                        color={filters.status === "active" ? "green" : "red"}
                      >
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} scenario
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Billing Scenarios ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No billing scenarios match the current filters"
                    : "No billing scenarios found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 150 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text
                            size="sm"
                            c={item.description ? undefined : "dimmed"}
                            lineClamp={2}
                          >
                            {item.description || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "orange" : "green"}
                                onClick={() => handleToggleActive(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleDelete(item)}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editing ? "Edit Billing Scenario" : "Add Billing Scenario"}
          size="md"
          closeOnClickOutside={false}
        >
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Name"
                placeholder="e.g., Medicare, Commercial Insurance"
                withAsterisk
                {...form.getInputProps("name")}
              />
              <Textarea
                label="Description"
                placeholder="Enter a description for this billing scenario"
                minRows={3}
                {...form.getInputProps("description")}
              />
              <Group justify="flex-end" mt="md" gap="xs">
                <Button variant="default" onClick={closeFormModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createScenario.isPending || updateScenario.isPending}
                >
                  {editing ? "Update" : "Create"}
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Billing Scenarios"
          size="sm"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Status
              </Text>
              <Group gap="xs">
                {(
                  [
                    { value: "all", label: "All" },
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ] as const
                ).map((opt) => (
                  <Button
                    key={opt.value}
                    variant={
                      (pendingFilters.status || "all") === opt.value
                        ? "filled"
                        : "light"
                    }
                    size="compact-sm"
                    color={
                      opt.value === "active"
                        ? "green"
                        : opt.value === "inactive"
                        ? "red"
                        : "gray"
                    }
                    onClick={() =>
                      setPendingFilters({
                        ...pendingFilters,
                        status: opt.value === "all" ? undefined : opt.value,
                      })
                    }
                  >
                    {opt.label}
                  </Button>
                ))}
              </Group>
            </Stack>
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={() => setPendingFilters({})}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Toggle Active Confirmation Modal */}
        <Modal
          opened={toggleModalOpened}
          onClose={closeToggleModal}
          title={
            itemToToggle?.isActive
              ? "Deactivate Billing Scenario"
              : "Activate Billing Scenario"
          }
          size="sm"
          centered
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {itemToToggle?.isActive ? "deactivate" : "activate"}{" "}
              <strong>{itemToToggle?.name}</strong>?
            </Text>
            {itemToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivating this scenario may affect existing configurations
                that rely on it.
              </Text>
            )}
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="default" onClick={closeToggleModal}>
                Cancel
              </Button>
              <Button
                color={itemToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggle}
                loading={toggleActive.isPending}
              >
                {itemToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title="Delete Billing Scenario"
          size="sm"
          centered
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to delete{" "}
              <strong>{itemToDelete?.name}</strong>?
            </Text>
            <Text size="sm" c="dimmed">
              This action cannot be undone.
            </Text>
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="default" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={deleteScenario.isPending}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
